import { useEffect, useMemo, useState } from "react";
import { api, ApiError } from "../api/client.js";
import { PageHeader, Panel, Spinner } from "../components/Panels.js";

interface StaffRow {
  id: string;
  full_name: string;
  email: string;
  role: string;
  manager_id: string | null;
  manager_name: string | null;
  business_unit_name: string | null;
}

interface StaffContextRow {
  id: string;
  staff_id: string;
  content_md: string;
  updated_by_staff_id: string | null;
  created_at: string;
  updated_at: string;
}

interface StaffContextResponse {
  ok: true;
  context: StaffContextRow | null;
}

const PLACEHOLDER = `## Role expectations

- What "good" looks like this quarter
- Specific blockers that should always escalate
- Stakeholders this person interacts with

## Tone

How Compass should phrase nudges and digest mentions for this person.`;

export function StaffContextPage() {
  const [staff, setStaff] = useState<StaffRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string>("");
  const [draft, setDraft] = useState<string>("");
  const [serverContext, setServerContext] = useState<StaffContextRow | null>(null);
  const [loadingContext, setLoadingContext] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ staff: StaffRow[] }>("/staff")
      .then((r) => setStaff(r.staff))
      .catch((e: Error) => setError(e.message));
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setServerContext(null);
      setDraft("");
      return;
    }
    setLoadingContext(true);
    setSaveMessage(null);
    api
      .get<StaffContextResponse>(`/settings/staff-context/${selectedId}`)
      .then((r) => {
        setServerContext(r.context);
        setDraft(r.context?.content_md ?? "");
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoadingContext(false));
  }, [selectedId]);

  const selected = useMemo(
    () => staff?.find((s) => s.id === selectedId) ?? null,
    [staff, selectedId],
  );

  const save = async () => {
    if (!selectedId) return;
    setSaving(true);
    setSaveMessage(null);
    try {
      await api.put(`/settings/staff-context/${selectedId}`, {
        content_md: draft,
      });
      const refreshed = await api.get<StaffContextResponse>(
        `/settings/staff-context/${selectedId}`,
      );
      setServerContext(refreshed.context);
      setSaveMessage("Saved.");
    } catch (e) {
      const apiErr = e as ApiError;
      const body = apiErr.body as { error?: string } | null;
      if (apiErr.status === 403 && body?.error === "forbidden") {
        setSaveMessage(
          "You can only edit context for your direct reports. Ask an executive to edit other staff.",
        );
      } else {
        setSaveMessage(apiErr.message);
      }
    } finally {
      setSaving(false);
    }
  };

  if (error) return <div className="p-6 text-sm text-sentiment-overwhelmed">{error}</div>;
  if (!staff) return <div className="p-6"><Spinner /></div>;

  const dirty = (serverContext?.content_md ?? "") !== draft;

  return (
    <>
      <PageHeader
        title="Per-staff context"
        subtitle="Role-specific expectations layered on top of the operating principles. Compass injects this into the extraction prompt so check-ins are interpreted against the right bar for each person."
      />
      <div className="px-6 grid grid-cols-3 gap-4">
        <Panel title="Choose staff" className="col-span-1">
          <select
            className="w-full bg-bg-base border border-bg-line2 rounded px-2 py-2 text-ink"
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
          >
            <option value="">— select a person —</option>
            {staff.map((s) => (
              <option key={s.id} value={s.id}>
                {s.full_name} · {s.role}
              </option>
            ))}
          </select>
          {selected ? (
            <dl className="mt-4 text-xs text-ink-dim space-y-1">
              <div>
                <dt className="inline text-ink-dimmer">Email: </dt>
                <dd className="inline font-mono">{selected.email}</dd>
              </div>
              <div>
                <dt className="inline text-ink-dimmer">Manager: </dt>
                <dd className="inline">{selected.manager_name ?? "—"}</dd>
              </div>
              <div>
                <dt className="inline text-ink-dimmer">BU: </dt>
                <dd className="inline">{selected.business_unit_name ?? "—"}</dd>
              </div>
              {serverContext ? (
                <div>
                  <dt className="inline text-ink-dimmer">Last edited: </dt>
                  <dd className="inline font-mono">
                    {serverContext.updated_at.slice(0, 16).replace("T", " ")}
                  </dd>
                </div>
              ) : (
                <div className="text-ink-dimmer">No context set yet.</div>
              )}
            </dl>
          ) : null}
        </Panel>

        <Panel
          title={selected ? `Context for ${selected.full_name}` : "Context"}
          action={
            selectedId ? (
              <button
                type="button"
                onClick={save}
                disabled={saving || !dirty}
                className="text-xs px-3 py-1 rounded-md bg-accent-cyan/20 text-accent-cyan border border-accent-cyan/40 disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save"}
              </button>
            ) : null
          }
          className="col-span-2"
        >
          {!selectedId ? (
            <div className="text-sm text-ink-dim">
              Pick someone on the left to edit their per-staff context.
            </div>
          ) : loadingContext ? (
            <Spinner />
          ) : (
            <>
              <textarea
                className="w-full bg-bg-base border border-bg-line2 rounded p-3 font-mono text-sm text-ink min-h-[420px]"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={PLACEHOLDER}
              />
              {saveMessage ? (
                <div className="mt-2 text-xs text-ink-dim">{saveMessage}</div>
              ) : null}
            </>
          )}
        </Panel>
      </div>
    </>
  );
}
